<template>
  <PageLayout :title="activeWorkspaceName || 'Workspace'" fluid>
    <template #actions>
      <div class="mission-toolbar">
        <span :class="wsConnected ? 'text-success' : 'text-danger'" class="mission-toolbar__live small" title="Live">
          <i class="fas fa-circle"></i>
        </span>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :title="theme === 'dark' ? 'Switch to light' : 'Switch to dark'"
          @click="setTheme(theme === 'dark' ? 'light' : 'dark')"
        >
          <i class="fas" :class="theme === 'dark' ? 'fa-sun' : 'fa-moon'"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          title="Refresh workspace"
          :disabled="refreshing"
          @click="refreshWorkspace"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          title="Fullscreen"
          @click="enterFullscreen"
        >
          <i class="fas fa-expand"></i>
        </button>
        <button
          class="btn btn-sm"
          :class="editMode ? 'btn-warning' : 'btn-outline-secondary'"
          title="Edit layout"
          @click="toggleEdit"
        >
          <i class="fas fa-th"></i>
          <span class="mission-toolbar__label">{{ editMode ? 'Done' : 'Edit' }}</span>
        </button>
        <div class="mission-menu" :class="{ 'is-open': menuOpen }">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="More"
            @click.stop="menuOpen = !menuOpen"
          >
            <i class="fas fa-ellipsis-v"></i>
          </button>
          <div v-if="menuOpen" class="mission-menu__dropdown" @click.stop>
            <button type="button" class="mission-menu__item" @click="onMenu('rename')">
              <i class="fas fa-pen"></i> Rename
            </button>
            <button type="button" class="mission-menu__item" :disabled="!layout" @click="onMenu('export')">
              <i class="fas fa-download"></i> Export JSON
            </button>
            <button type="button" class="mission-menu__item" @click="onMenu('import')">
              <i class="fas fa-upload"></i> Import JSON
            </button>
            <button
              v-if="canInstallPWA"
              type="button"
              class="mission-menu__item"
              @click="onMenu('install')"
            >
              <i class="fas fa-download"></i> Install App
            </button>
          </div>
        </div>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          @change="onImportFile"
        >
      </div>
    </template>

    <div
      class="mission-shell workspace-theme"
      :class="{ 'mission-shell--fs': fullscreen }"
      :data-ws-theme="theme"
    >
      <div v-if="fullscreen" class="mission-fs__bar">
        <div class="mission-fs__title">
          <strong>{{ activeWorkspaceName || 'Workspace' }}</strong>
          <span :class="wsConnected ? 'text-success' : 'text-danger'" class="small ml-2">
            <i class="fas fa-circle mission-dot"></i> Live
          </span>
        </div>
        <div class="mission-fs__actions">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :title="theme === 'dark' ? 'Switch to light' : 'Switch to dark'"
            @click="setTheme(theme === 'dark' ? 'light' : 'dark')"
          >
            <i class="fas" :class="theme === 'dark' ? 'fa-sun' : 'fa-moon'"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="Refresh workspace"
            :disabled="refreshing"
            @click="refreshWorkspace"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            title="Exit fullscreen (Esc)"
            @click="exitFullscreen"
          >
            <i class="fas fa-compress"></i>
          </button>
        </div>
      </div>

      <div class="mission-control" :class="{ 'mission-control--fs': fullscreen }">
        <div class="mission-chrome">
          <ServicePresetBar />
          <WorkspaceTabs />
        </div>
        <div class="row mission-control__row">
          <div v-if="editMode && !fullscreen" class="col-12 col-xl-2 mb-3 mission-palette-col">
            <ContentCard>
              <WidgetPalette @add="onAddWidget" @preset="onPreset" />
            </ContentCard>
          </div>
          <div :class="editMode && !fullscreen ? 'col-12 col-xl-10' : 'col-12'">
            <WorkspaceCanvas ref="canvas" :fullscreen="fullscreen" />
          </div>
        </div>
      </div>
    </div>

    <modal name="rename-workspace" height="auto" :width="420" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-3">Rename Workspace</h5>
        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input
            ref="renameInput"
            v-model="renameForm.name"
            class="form-control"
            maxlength="100"
            placeholder="Workspace name"
            @keyup.enter="saveRename"
          >
        </div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('rename-workspace')">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!renameForm.name.trim() || renaming"
            @click="saveRename"
          >
            {{ renaming ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import WorkspaceCanvas from './components/workspace/WorkspaceCanvas.vue'
import WidgetPalette from './components/workspace/WidgetPalette.vue'
import WorkspaceTabs from './components/workspace/WorkspaceTabs.vue'
import ServicePresetBar from './components/workspace/ServicePresetBar.vue'
import {
  applyKioskClasses,
  canInstallPWA,
  enterNativeFullscreen,
  exitNativeFullscreen,
  isStandaloneDisplay,
  promptPWAInstall,
} from '@/lib/pwa'

export default {
  components: { WorkspaceCanvas, WidgetPalette, WorkspaceTabs, ServicePresetBar },
  data() {
    return {
      fullscreen: false,
      canInstallPWA: false,
      renameForm: { name: '', id: null },
      renaming: false,
      menuOpen: false,
      refreshing: false,
    }
  },
  computed: {
    ...mapGetters('mission', ['wsConnected']),
    ...mapGetters('workspace', [
      'editMode',
      'activeWorkspaceName',
      'activeWorkspaceId',
      'theme',
      'layout',
    ]),
    workspaceId() {
      return Number(this.$route.params.id)
    },
  },
  watch: {
    workspaceId: {
      immediate: true,
      handler(id) {
        if (id) this.loadWorkspace(id)
      },
    },
    activeWorkspaceName(name) {
      if (!name) return
      document.title = `${name} · ControlLabs`
    },
  },
  async mounted() {
    await this.fetchWorkspaces()
    await this.fetchCatalog()
    await this.fetchNodes()
    await this.fetchMessages()
    this.connectWS()
    window.addEventListener('keydown', this.onKeydown)
    document.addEventListener('click', this.closeMenu)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    window.addEventListener('pwa-install-available', this.refreshInstallOffer)
    this.refreshInstallOffer()
    if (isStandaloneDisplay()) {
      this.enterFullscreen()
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('click', this.closeMenu)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    window.removeEventListener('pwa-install-available', this.refreshInstallOffer)
    this.unlockBody()
    this.disconnectWS()
    if (this.editMode) this.toggleEditMode()
  },
  methods: {
    ...mapActions('mission', ['fetchMessages', 'fetchNodes', 'connectWS', 'disconnectWS']),
    ...mapActions('workspace', [
      'fetchWorkspaces',
      'fetchLayout',
      'fetchCatalog',
      'toggleEditMode',
      'addWidget',
      'applyPreset',
      'setTheme',
      'renameWorkspace',
      'importLayout',
    ]),
    closeMenu() {
      this.menuOpen = false
    },
    onMenu(action) {
      this.menuOpen = false
      if (action === 'rename') this.openRename()
      else if (action === 'export') this.exportLayoutJson()
      else if (action === 'import') this.$refs.importFile?.click()
      else if (action === 'install') this.installApp()
    },
    refreshInstallOffer() {
      this.canInstallPWA = canInstallPWA()
    },
    async installApp() {
      const ok = await promptPWAInstall()
      this.refreshInstallOffer()
      if (ok) this.$toast?.success?.('App installed — buka dari home screen untuk fullscreen')
    },
    async loadWorkspace(id) {
      await this.$store.dispatch('workspace/flushSave')
      await this.fetchLayout(id)
    },
    toggleEdit() {
      this.toggleEditMode()
    },
    onAddWidget(type) {
      this.addWidget({ type, config: {} })
    },
    onPreset(key) {
      this.applyPreset(key)
    },
    openRename() {
      this.renameForm = {
        name: this.activeWorkspaceName || '',
        id: this.activeWorkspaceId,
      }
      this.$modal.show('rename-workspace')
      this.$nextTick(() => {
        this.$refs.renameInput?.focus?.()
        this.$refs.renameInput?.select?.()
      })
    },
    async saveRename() {
      const name = this.renameForm.name.trim()
      if (!name || this.renaming) return
      this.renaming = true
      try {
        await this.renameWorkspace(name)
        this.$toast?.success?.('Workspace renamed')
        this.$modal.hide('rename-workspace')
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || 'Failed to rename')
      } finally {
        this.renaming = false
      }
    },
    exportLayoutJson() {
      if (!this.layout) return
      const payload = {
        version: 1,
        exportedAt: new Date().toISOString(),
        workspace: {
          id: this.activeWorkspaceId,
          name: this.activeWorkspaceName || 'Workspace',
        },
        layout: this.layout,
      }
      const name = String(this.activeWorkspaceName || 'workspace')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'workspace'
      const filename = `${name}-${this.activeWorkspaceId || 'layout'}.json`
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    async onImportFile(e) {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      try {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(String(reader.result || ''))
          reader.onerror = () => reject(new Error('Failed to read file'))
          reader.readAsText(file)
        })
        await this.importLayout(text)
        this.$toast?.success?.('Layout imported')
      } catch (err) {
        this.$toast?.error?.(err.message || 'Failed to import JSON')
      }
    },
    async enterFullscreen() {
      if (this.editMode) this.toggleEditMode()
      this.fullscreen = true
      applyKioskClasses(true)
      await enterNativeFullscreen()
      this.$nextTick(() => this.$refs.canvas?.remeasure?.())
    },
    async exitFullscreen() {
      this.fullscreen = false
      applyKioskClasses(false)
      await exitNativeFullscreen()
      this.$nextTick(() => this.$refs.canvas?.remeasure?.())
    },
    unlockBody() {
      applyKioskClasses(false)
    },
    onFullscreenChange() {
      if (!document.fullscreenElement && this.fullscreen && !isStandaloneDisplay()) {
        this.fullscreen = false
        applyKioskClasses(false)
      }
      this.$nextTick(() => this.$refs.canvas?.remeasure?.())
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.fullscreen) {
        if (document.fullscreenElement) return
        this.exitFullscreen()
      }
    },
    async refreshWorkspace() {
      if (this.refreshing || !this.workspaceId) return
      this.refreshing = true
      try {
        await this.$store.dispatch('workspace/flushSave')
        await Promise.all([
          this.fetchLayout(this.workspaceId),
          this.fetchNodes(),
          this.fetchMessages(),
        ])
        this.disconnectWS()
        this.connectWS()
        this.$refs.canvas?.remeasure?.()
        window.dispatchEvent(new CustomEvent('workspace-refresh'))
        this.$toast?.success?.('Workspace refreshed')
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || 'Failed to refresh')
      } finally {
        this.refreshing = false
      }
    },
  },
}
</script>

<style scoped>
.mission-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
}
.mission-toolbar__live {
  display: inline-flex;
  align-items: center;
  margin-right: 0.15rem;
}
.mission-toolbar__live .fa-circle,
.mission-dot {
  font-size: 0.4rem;
}
.mission-toolbar .btn {
  min-height: 34px;
  min-width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}
.mission-toolbar__label {
  display: none;
}
.mission-menu {
  position: relative;
}
.mission-menu__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 40;
  min-width: 160px;
  padding: 0.35rem;
  background: #fff;
  border: 1px solid #e3e6ef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
.mission-menu__item {
  appearance: none;
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #272b41;
  text-align: left;
  cursor: pointer;
}
.mission-menu__item:hover:not(:disabled) {
  background: #f4f6f9;
}
.mission-menu__item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.mission-menu__item i {
  width: 1rem;
  text-align: center;
  color: #6c757d;
}

.mission-control { min-height: 70vh; }

.mission-chrome {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0;
  margin-bottom: 0.5rem;
}
.mission-chrome >>> .service-bar-wrap {
  margin-bottom: 0;
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
}
.mission-chrome >>> .service-bar-wrap.is-open {
  flex: 1 1 100%;
  margin-bottom: 0.4rem;
}
.mission-chrome >>> .service-bar-toggle {
  width: 40px;
  min-height: 40px;
  height: auto;
  border-radius: 8px 0 0 8px;
  border-color: var(--ws-widget-border, #e9ecef);
  border-right: 0;
  background: var(--ws-widget-header-bg, #f8f9fa);
}
.mission-chrome >>> .service-bar {
  width: 100%;
}
.mission-chrome >>> .ws-tabs {
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
}
.mission-chrome:has(.service-bar-toggle) >>> .ws-tabs {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.mission-shell--fs {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  background: var(--ws-panel, #f4f6f9);
}

.mission-shell--fs[data-ws-theme='dark'] {
  background: var(--ws-canvas-bg, #141414);
}

.mission-fs__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid var(--ws-widget-border, #e9ecef);
  background: var(--ws-widget-bg, #fff);
  color: var(--ws-text, #272b41);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.mission-fs__title {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mission-fs__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.35rem;
}

.mission-control--fs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0.5rem;
}

.mission-control--fs >>> .mission-chrome {
  flex-shrink: 0;
}

.mission-control--fs >>> .mission-control__row {
  flex: 1;
  min-height: 0;
  margin: 0;
  overflow: hidden;
}

.mission-control--fs >>> .mission-control__row > [class*='col-'] {
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  padding-left: 0;
  padding-right: 0;
}

.mission-control--fs >>> .workspace-canvas {
  min-height: 0 !important;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.mission-control--fs >>> .workspace-canvas__fit {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

.mission-control--fs >>> .vue-grid-layout {
  height: auto !important;
  max-height: 100%;
  width: 100% !important;
  max-width: 100%;
  overflow: visible !important;
}

.mission-control--fs >>> .vue-grid-item {
  overflow: hidden !important;
}

.mission-control--fs >>> .widget-wrapper {
  overflow: hidden !important;
}

.mission-control--fs >>> .widget-wrapper__body {
  overflow: auto !important;
}

@media (min-width: 992px) {
  .mission-toolbar__label {
    display: inline;
  }
}

@media (max-width: 1199.98px) {
  .mission-toolbar {
    justify-content: flex-start;
  }
  .mission-control {
    min-height: 60vh;
  }
  .mission-control--fs {
    padding: 0.35rem;
  }
  .mission-fs__bar {
    padding: 0.4rem 0.55rem;
  }
  .mission-fs__title strong {
    font-size: 0.95rem;
  }
  .mission-palette-col >>> .card,
  .mission-palette-col >>> .card-body {
    padding: 0.5rem !important;
  }
}

@media (max-width: 767.98px) {
  .mission-toolbar .btn {
    min-height: 40px;
    min-width: 40px;
  }
}
</style>

<style>
html.workspace-fs-active,
body.workspace-fs-active {
  overflow: hidden !important;
  height: 100% !important;
  width: 100% !important;
  overscroll-behavior: none;
}

/* Fullscreen page chrome: no page scroll; widgets keep slim themed bars */
.mission-shell--fs {
  scrollbar-width: none;
}
.mission-shell--fs::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}
</style>
